import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import Loader from "./Loader";
import LabelWithInputField from "./LabelWithInputField";
import ListWrapper from "../utils/ListWrapper";
import CheckServerErrors from "./CheckServerErrors";
import LoginResult from "../classes/LoginResult";
import TextView from "./TextView";
import PageNavigator from "../classes/PageNavigator";
import Admin from "../models/Admin";
import CollectionUtils from "../utils/CollectionUtils";
import Query from "../classes/Query";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

type _LoginButtonOnPressed = (d3eState: AdminLoginPageRefs) => void;

type _SignupButtonOnPressed = (d3eState: AdminLoginPageRefs) => void;

type _EmailFieldOnChanged = (
  text: string,
  d3eState: AdminLoginPageRefs
) => void;

type _PasswordFieldOnChanged = (
  text: string,
  d3eState: AdminLoginPageRefs
) => void;

export interface AdminLoginPageProps extends BaseUIProps {
  key?: string;
}
/// To store state data for AdminLoginPage
class AdminLoginPageRefs {
  public loginButton: LoginButtonState = new LoginButtonState();
  public signupButton: SignupButtonState = new SignupButtonState();
}

interface SignupButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminLoginPageRefs;
  _signUpButtonHandler?: _SignupButtonOnPressed;
}

class SignupButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _SignupButtonWithState extends ObservableComponent<SignupButtonWithStateProps> {
  signupButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SignupButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get signupButton(): SignupButtonState {
    return this.props.d3eState.signupButton;
  }
  public get d3eState(): AdminLoginPageRefs {
    return this.props.d3eState;
  }
  public get _signUpButtonHandler(): _SignupButtonOnPressed {
    return this.props._signUpButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("signupButton", null, this.signupButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["signupButton"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(2.0, 0.0, 0.0, 0.0, new Map()),
      child: Button({
        disable: this.signupButton.disable,
        onPressed: () => {
          this._signUpButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({
          data: "Sign Up",
          style: new ui.TextStyle({ fontSize: 16 }),
          className: "x17a",
        }),
      }),
      className: "xb0fd",
    });
  }
}
function SignupButtonWithState(props: SignupButtonWithStateProps) {
  return React.createElement(_SignupButtonWithState, props);
}

interface LoginButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminLoginPageRefs;
  _onloginButtonPressed?: _LoginButtonOnPressed;
  showLoader: boolean;
}

class LoginButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _LoginButtonWithState extends ObservableComponent<LoginButtonWithStateProps> {
  loginButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: LoginButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get loginButton(): LoginButtonState {
    return this.props.d3eState.loginButton;
  }
  public get showLoader(): boolean {
    return this.props.showLoader;
  }
  public get d3eState(): AdminLoginPageRefs {
    return this.props.d3eState;
  }
  public get _onloginButtonPressed(): _LoginButtonOnPressed {
    return this.props._onloginButtonPressed;
  }
  public initState() {
    super.initState();

    this.updateObservable("loginButton", null, this.loginButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["loginButton", "loginButton.", "loginButton.hover", "showLoader"],
      this.rebuild
    );
  }
  public loginButtonOnEnter(event): void {
    return this.loginButton.setHover(true);
  }
  public loginButtonOnExit(event): void {
    return this.loginButton.setHover(false);
  }
  public dispose(): void {
    this.loginButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      expand: true,
      child: Button({
        padding: this.loginButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.loginButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.loginButton.disable,
        onPressed: () => {
          this._onloginButtonPressed(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.center,
          crossAxisAlignment: ui.CrossAxisAlignment.center,
          children: [
            this.showLoader ? Loader({}) : [],
            TextView({ data: "Login", className: "xea", key: "1" }),
          ],
          className: "xa0 hc h",
        }),
        onEnter: (event) => {
          this.loginButtonOnEnter(event);
        },
        onExit: (event) => {
          this.loginButtonOnExit(event);
        },
      }),
      className: "xd71 hc h",
    });
  }
}
function LoginButtonWithState(props: LoginButtonWithStateProps) {
  return React.createElement(_LoginButtonWithState, props);
}

class _AdminLoginPageState extends ObservableComponent<AdminLoginPageProps> {
  d3eState: AdminLoginPageRefs = new AdminLoginPageRefs();
  email: string = "";
  password: string = "";
  emailError: boolean = false;
  passwordError: boolean = false;
  showLoader: boolean = false;
  loginErrors: Array<string> = ListWrapper.widget(this, "loginErrors");
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AdminLoginPageProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "email",
        "emailError",
        "loginErrors",
        "password",
        "passwordError",
        "showLoader",
      ],
      this.rebuild
    );
  }
  public setEmail(val: string): void {
    let isValChanged: boolean = this.email !== val;

    if (!isValChanged) {
      return;
    }

    this.email = val;

    this.fire("email", this);
  }
  public setPassword(val: string): void {
    let isValChanged: boolean = this.password !== val;

    if (!isValChanged) {
      return;
    }

    this.password = val;

    this.fire("password", this);
  }
  public setEmailError(val: boolean): void {
    let isValChanged: boolean = this.emailError !== val;

    if (!isValChanged) {
      return;
    }

    this.emailError = val;

    this.fire("emailError", this);
  }
  public setPasswordError(val: boolean): void {
    let isValChanged: boolean = this.passwordError !== val;

    if (!isValChanged) {
      return;
    }

    this.passwordError = val;

    this.fire("passwordError", this);
  }
  public setShowLoader(val: boolean): void {
    let isValChanged: boolean = this.showLoader !== val;

    if (!isValChanged) {
      return;
    }

    this.showLoader = val;

    this.fire("showLoader", this);
  }
  public setLoginErrors(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.loginErrors,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.loginErrors.clear();

    this.loginErrors.addAll(val);

    this.fire("loginErrors", this);
  }
  public addToLoginErrors(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.loginErrors.contains(val)) this.loginErrors.add(val);
    } else {
      this.loginErrors.remove(this.loginErrors.elementAt(index));

      this.loginErrors.add(val);
    }

    this.fire("loginErrors", this, val, true);
  }
  public removeFromLoginErrors(val: string): void {
    this.loginErrors.remove(val);

    this.fire("loginErrors", this, val, false);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Row({
      children: [
        ui.Column({
          mainAxisAlignment: ui.MainAxisAlignment.center,
          children: [
            ui.AssetImage({
              path: "images/LoginImg.png",
              fit: ui.BoxFit.contain,
              height: 700,
              width: 700,
              className: "xff60 hc vc",
              key: "0",
            }),
          ],
          className: "xd2 hc vc h v",
          key: "0",
        }),
        ui.Column({
          mainAxisAlignment: ui.MainAxisAlignment.center,
          children: [
            ui.Column({
              mainAxisAlignment: ui.MainAxisAlignment.center,
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                TextView({
                  data: "Login",
                  style: new ui.TextStyle({
                    color: new ui.Color(0xff000000),
                    fontSize: 28,
                    fontWeight: ui.FontWeight.w900,
                  }),
                  className: "xa3a hc",
                  key: "0",
                }),
                ui.Container({
                  padding: ui.EdgeInsets.fromLTRB(
                    0.0,
                    0.0,
                    0.0,
                    10.0,
                    new Map()
                  ),
                  child: LabelWithInputField({
                    name: "Email",
                    placeHolder: "Enter email",
                    value: this.email,
                    errors: this.emailError ? ["Please Enter Email"] : [],
                    onChanged: (text) => {
                      this.emailFieldonChanged(text, this.d3eState);
                    },
                  }),
                  key: "1",
                  className: "x16 hc h",
                }),
                ui.Container({
                  padding: ui.EdgeInsets.fromLTRB(
                    0.0,
                    0.0,
                    0.0,
                    20.0,
                    new Map()
                  ),
                  child: LabelWithInputField({
                    name: "Password",
                    placeHolder: "Enter password",
                    obscureText: true,
                    value: this.password,
                    errors: this.passwordError ? ["Please Enter Email"] : [],
                    onChanged: (text) => {
                      this.passwordFieldonChanged(text, this.d3eState);
                    },
                  }),
                  key: "2",
                  className: "xbf hc h",
                }),
                this.loginErrors.isNotEmpty
                  ? CheckServerErrors({
                      errors: this.loginErrors,
                      className: "x15 hc",
                    })
                  : [],
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.center,
                  crossAxisAlignment: ui.CrossAxisAlignment.center,
                  children: [
                    LoginButtonWithState({
                      d3eState: this.d3eState,
                      _onloginButtonPressed: this.onloginButtonPressed,
                      showLoader: this.showLoader,
                      key: "0",
                    }),
                  ],
                  className: "x8a12 hc h",
                  key: "4",
                }),
                ui.Column({
                  mainAxisAlignment: ui.MainAxisAlignment.end,
                  crossAxisAlignment: ui.CrossAxisAlignment.end,
                  children: [
                    ui.Row({
                      mainAxisAlignment: ui.MainAxisAlignment.center,
                      children: [
                        TextView({
                          data: "Don\u2019t have an account?",
                          style: new ui.TextStyle({
                            color: cStyle.c11,
                            fontWeight: ui.FontWeight.w500,
                            fontSize: cStyle.tTextViewHeadlineFiveFontSizeOn,
                          }),
                          className: "x5a4",
                          key: "0",
                        }),
                        SignupButtonWithState({
                          d3eState: this.d3eState,
                          _signUpButtonHandler: this.signUpButtonHandler,
                          key: "1",
                        }),
                      ],
                      className: "xe8d hc h",
                      key: "0",
                    }),
                  ],
                  className: "x29 hc h",
                  key: "5",
                }),
              ],
              className: "x2be7 hc vc h v",
              key: "0",
            }),
          ],
          className: "xa8f hc vc h v",
          key: "1",
        }),
      ],
      className: ui.join(this.props.className, "AdminLoginPage hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onloginButtonPressed = (d3eState: AdminLoginPageRefs): void => {
    this.setEmailError(false);

    if (this.email === "") {
      this.setEmailError(true);
    } else {
      this.doAuthenticateUser();
    }
  };
  public signUpButtonHandler = (d3eState: AdminLoginPageRefs): void => {
    this.navigator.pushAdminSignupPage();
  };
  public doAuthenticateUser = async (): Promise<void> => {
    this.setShowLoader(true);

    let loginResult: LoginResult =
      await Query.get().loginAdminUserWithEmailAndPassword(
        UsageConstants.QUERY_LOGINADMINUSERWITHEMAILANDPASSWORD_ADMINLOGINPAGE_EVENTHANDLERS_DOAUTHENTICATEUSER_BLOCK,
        { email: this.email.toLowerCase(), password: this.password }
      );

    if (loginResult.success) {
      this.navigator.pushAdminHomePage({
        admin: loginResult.userObject as Admin,
      });
    } else {
      // Handle login errors
    }

    this.setShowLoader(false);
  };
  public emailFieldonChanged = (
    val: string,
    d3eState: AdminLoginPageRefs
  ): void => {
    this.setEmail(val);
  };
  public passwordFieldonChanged = (
    val: string,
    d3eState: AdminLoginPageRefs
  ): void => {
    this.setPassword(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get loginButton() {
    return this.d3eState.loginButton;
  }
  public get signupButton() {
    return this.d3eState.signupButton;
  }
}
export default function AdminLoginPage(props: AdminLoginPageProps) {
  return React.createElement(_AdminLoginPageState, props);
}
